import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import './GitaChat.css';

// Simple markdown parser for basic formatting
const parseMarkdown = (text) => {
  if (!text) return '';
  
  return text
    // Bold text: **text** or __text__
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    // Italic text: *text* or _text_
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    // Headers: ### Header
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h4>$1</h4>')
    .replace(/^# (.*$)/gim, '<h5>$1</h5>')
    // Line breaks
    .replace(/\n/g, '<br>')
    // Sanskrit quotes (special formatting)
    .replace(/<div class="sanskrit-quote">/g, '<div class="sanskrit-quote">')
    .replace(/<\/div>/g, '</div>')
    // Add special styling for Sanskrit text after "Sanskrit:" headers
    .replace(/(<strong>Sanskrit:<\/strong><br>)(.*?)(<br>|$)/g, '$1<span class="sanskrit-text">$2</span>$3');
};

const GitaChat = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  
  const messageInputRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const inputContainerRef = useRef(null);
  const welcomeScreenRef = useRef(null);

  // Initialize session on component mount
  useEffect(() => {
    initializeSession();
  }, []);

  const initializeSession = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CHAT_SESSION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success) {
        setSessionId(data.sessionId);
      }
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const handleInput = () => {
    const textarea = messageInputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const sendMessage = async () => {
    const message = inputValue.trim();
    if (!message || !sessionId) return;

    // Hide welcome screen and move input to bottom on first message
    if (isFirstMessage) {
      setIsFirstMessage(false);
      if (welcomeScreenRef.current) {
        welcomeScreenRef.current.style.opacity = '0';
      }
      if (inputContainerRef.current) {
        inputContainerRef.current.classList.remove('initial');
      }
      
      setTimeout(() => {
        if (welcomeScreenRef.current) {
          welcomeScreenRef.current.style.display = 'none';
        }
        if (chatMessagesRef.current) {
          chatMessagesRef.current.style.display = 'block';
        }
      }, 500);
    }

    // Add user message
    const userMessage = {
      id: Date.now(),
      content: message,
      type: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    // Clear input
    setInputValue('');
    if (messageInputRef.current) {
      messageInputRef.current.style.height = 'auto';
    }

    // Show loading indicator
    setIsLoading(true);

    try {
      // Send message to backend
      const response = await fetch(API_ENDPOINTS.RAG_FAST, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: message,
          topK: 5
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Format the response
        const formattedResponse = formatGitaResponse(data);
        
        const gitaMessage = {
          id: Date.now() + 1,
          content: formattedResponse,
          type: 'gita',
          timestamp: new Date(),
          citations: data.citations,
          shlok: data.shlok
        };
        
        setMessages(prev => [...prev, gitaMessage]);
      } else {
        // Handle error
        const errorMessage = {
          id: Date.now() + 1,
          content: "I apologize, but I encountered an error while processing your question. Please try again.",
          type: 'gita',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        content: "I apologize, but I'm having trouble connecting right now. Please try again later.",
        type: 'gita',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatGitaResponse = (data) => {
    let response = data.finalAnswer || "I apologize, but I couldn't find a specific answer to your question.";
    
    // Add Sanskrit quotes if available
    if (data.shlok && data.shlok.length > 0) {
      const shlokQuotes = data.shlok.map(shlok => `
        <div class="sanskrit-quote">
          <div class="sanskrit-text">${shlok.sanskrit || ''}</div>
          <div class="verse-ref">${shlok.reference}</div>
        </div>
      `).join('');
      
      response += shlokQuotes;
    }
    
    return response;
  };

  const sendSuggestion = (text) => {
    setInputValue(text);
    // Trigger input change to enable submit button
    setTimeout(() => {
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    }, 100);
  };

  const suggestions = [
    {
      icon: '☸',
      text: 'What is the essence of karma yoga?'
    },
    {
      icon: '☮',
      text: 'How can I find peace in difficult times?'
    },
    {
      icon: '📿',
      text: 'Explain Chapter 2, Verse 47'
    },
    {
      icon: '✦',
      text: 'What does Krishna say about the eternal soul?'
    }
  ];

  return (
    <div className="gita-chat">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/')}>
            ←
          </button>
          <div className="header-title">
            <h1>Bhagavad Gita</h1>
            <span className="header-subtitle">श्रीमद्भगवद्गीता</span>
          </div>
        </div>
        <div className="header-actions">
          <button className="icon-btn" title="New Chat">⊕</button>
          <button className="icon-btn" title="History">☰</button>
        </div>
      </header>

      {/* Chat Container */}
      <div className="chat-container">
        {/* Welcome Screen */}
        <div className="welcome-screen" ref={welcomeScreenRef}>
          <div className="welcome-om">ॐ</div>
          <h2 className="welcome-title">Welcome to the Bhagavad Gita</h2>
          <p className="welcome-sanskrit">कर्मण्येवाधिकारस्ते मा फलेषु कदाचन</p>
          <p className="welcome-description">
            I am here to guide you through Krishna's eternal wisdom. Ask me about dharma, karma, yoga, 
            the nature of reality, or any verse from the sacred dialogue. Let us explore together.
          </p>
          
          <div className="suggestions">
            {suggestions.map((suggestion, index) => (
              <div 
                key={index}
                className="suggestion-card" 
                onClick={() => sendSuggestion(suggestion.text)}
              >
                <div className="suggestion-icon">{suggestion.icon}</div>
                <div className="suggestion-text">{suggestion.text}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Chat Messages */}
        <div className="chat-messages" ref={chatMessagesRef}>
          {messages.map((message) => (
            <div key={message.id} className={`message message-${message.type}`}>
              {message.type === 'user' ? (
                <div className="message-content">{message.content}</div>
              ) : (
                <>
                  <div className="gita-avatar">ॐ</div>
                  <div 
                    className="message-content"
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(message.content) }}
                  />
                </>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="message message-gita typing-indicator">
              <div className="gita-avatar">ॐ</div>
              <div className="message-content">
                <div className="loading-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input Container */}
      <div className={`input-container ${isFirstMessage ? 'initial' : ''}`} ref={inputContainerRef}>
        <div className="input-wrapper">
          <textarea 
            ref={messageInputRef}
            className="input-box" 
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onInput={handleInput}
            onKeyDown={handleKeyPress}
            placeholder="Ask your question about the Bhagavad Gita..."
            rows="1"
          />
          <button 
            className={`submit-btn ${inputValue.trim() ? 'active' : ''}`}
            onClick={sendMessage}
            disabled={!inputValue.trim() || isLoading}
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
};

export default GitaChat; 